import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Component, HostBinding, inject, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { select, Store } from '@ngrx/store';
import {
  addGerberFile,
  addLectraFile,
  loadMonthlyConvertedFiles,
} from '../../../state/home.actions';
import { IndexedDbService } from '../../../data/services/indexed-db.service';
import { detectConversionMode } from '../../../../../common/helpers/conversion-utils';
import {
  selectGerberFiles,
  selectLectraFiles,
  selectMonthlyConvertedFiles,
} from '../../../state/home.selectors';
import { combineLatest, Observable, take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { selectAuthUser } from '../../../../auth/state/auth.selectors';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { UploadedFile } from '../../../models/uploaded-file';
import { JobStatus } from '../../../models/job-status';

@Component({
  selector: 'app-file-box',
  standalone: true,
  imports: [
    NgxFileDropModule,
    CommonModule,
    HttpClientModule,
    FormsModule,
    MatButtonToggleModule,
    MatIconModule,
    TranslatePipe,
    ConfirmationModalComponent,
  ],
  templateUrl: './file-box.component.html',
  styleUrl: './file-box.component.scss',
})
export class FileBoxComponent implements OnInit {
  http = inject(HttpClient);
  indexedDbService = inject(IndexedDbService);
  store = inject(Store);
  translate = inject(TranslateService);
  @HostBinding('class.compact') isCompact = false;
  protected user$ = this.store.pipe(select(selectAuthUser));
  protected monthlyConvertedFiles$: Observable<{ count: number }> =
    new Observable<{ count: number }>();
  protected showQueueLimitModal = false;
  protected showQueueLimitModalWithSlots = false;
  protected availableSlots = 0;
  protected MAX_FILE_COUNT = 50;
  private toastr = inject(ToastrService);
  private MAX_FILE_SIZE = 4 * 1024 * 1024;

  @Input() set compactMode(enabled: boolean) {
    this.isCompact = enabled;
  }

  ngOnInit(): void {
    this.store.dispatch(loadMonthlyConvertedFiles());
    this.monthlyConvertedFiles$ = this.store.select(
      selectMonthlyConvertedFiles,
    );
  }

  onQueueLimitModalClosed() {
    this.showQueueLimitModal = false;
  }

  onQueueLimitModalWithSlotsClosed() {
    this.showQueueLimitModalWithSlots = false;
  }

  public dropped(files: NgxFileDropEntry[]) {
    this.user$.pipe(take(1)).subscribe((user) => {
      if (!user) {
        console.log('User not logged in');
        return;
      }
    });

    combineLatest([
      this.store.pipe(select(selectGerberFiles), take(1)),
      this.store.pipe(select(selectLectraFiles), take(1)),
    ]).subscribe(([gerberFiles, lectraFiles]) => {
      const currentFileCount = gerberFiles.length + lectraFiles.length;
      const newFileCount = files.length + currentFileCount;
      const availableSlots = this.MAX_FILE_COUNT - currentFileCount;

      if (newFileCount > this.MAX_FILE_COUNT) {
        if (currentFileCount <= 0) {
          this.showQueueLimitModal = true;
        } else {
          this.availableSlots = availableSlots;
          this.showQueueLimitModalWithSlots = true;
        }
        return;
      }

      this.processFiles(files, availableSlots);
    });
  }

  private processFiles(files: NgxFileDropEntry[], availableSlots: number) {
    const filesToProcess = files.slice(0, availableSlots);

    for (const droppedFile of filesToProcess) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file(async (file: File) => {
          if (file.size > this.MAX_FILE_SIZE) {
            this.toastr.error(this.translate.instant('toastr.file_too_large'));
            return;
          }

          const fileCard: UploadedFile = {
            conversionId: crypto.randomUUID(),
            fileId: crypto.randomUUID(),
            fileName: file.name,
            index: fileEntry.file.length,
            status: JobStatus.Wait,
            queuePosition: 0,
            fileSize: file.size,
            conversionMode: detectConversionMode(file.name),
          };

          if (fileCard.conversionMode === 'Unknown') {
            this.toastr.error(this.translate.instant('toastr.invalid_format'));
            return;
          } else {
            await this.indexedDbService.addOrUpdateFileData(
              fileCard.fileId,
              file,
            );
            if (fileCard.conversionMode === 'Gerber') {
              this.store.dispatch(addGerberFile({ gerberFile: fileCard }));
            } else if (fileCard.conversionMode === 'Lectra') {
              this.store.dispatch(addLectraFile({ lectraFile: fileCard }));
            }
          }
        });
      }
    }
  }
}
